import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';

const VARIANT_STYLES = {
  default: {
    color: 'var(--text-muted)',
    hoverBg: 'var(--bg-input)',
    hoverColor: 'var(--text-primary)',
  },
  primary: {
    color: 'var(--text-muted)',
    hoverBg: 'var(--bg-input)',
    hoverColor: 'var(--text-primary)',
  },
  secondary: {
    color: 'var(--text-secondary)',
    hoverBg: 'var(--bg-card-alt)',
    hoverColor: 'var(--text-primary)',
  },
  danger: { color: '#ef4444', hoverBg: 'rgba(239,68,68,0.1)', hoverColor: '#dc2626' },
  ghost: {
    color: 'var(--text-ghost)',
    hoverBg: 'var(--bg-input)',
    hoverColor: 'var(--text-primary)',
  },
  ghostPrimary: { color: 'var(--text-ghost)', hoverBg: 'transparent', hoverColor: 'var(--brand)' },
  ghostDanger: { color: 'var(--text-ghost)', hoverBg: 'transparent', hoverColor: '#ef4444' },
  fillPrimary: { color: '#0A1610', bg: 'var(--accent)', hoverBg: 'var(--brand)' },
  fillDanger: { color: '#fff', bg: '#dc2626', hoverBg: '#b91c1c' },
};

const SIZE_PADDING = {
  sm: '0.375rem',
  md: '0.5rem',
  lg: '0.625rem',
};

const ICON_SIZE = {
  sm: '1rem',
  md: '1.125rem',
  lg: '1.25rem',
};

const IconButton = ({
  icon: Icon,
  tooltip,
  onClick,
  variant = 'default',
  size = 'md',
  tooltipPosition = 'top',
  buttonType = 'square',
  className,
  loading = false,
  'aria-label': ariaLabelProp,
  ...props
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, transform: '' });
  const buttonRef = useRef(null);

  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.default;

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    let top, left, transform;
    switch (tooltipPosition) {
      case 'bottom':
        top = rect.bottom + 8;
        left = rect.left + rect.width / 2;
        transform = 'translate(-50%, 0)';
        break;
      case 'left':
        top = rect.top + rect.height / 2;
        left = rect.left - 8;
        transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        top = rect.top + rect.height / 2;
        left = rect.right + 8;
        transform = 'translate(0, -50%)';
        break;
      default:
        top = rect.top - 8;
        left = rect.left + rect.width / 2;
        transform = 'translate(-50%, -100%)';
        break;
    }
    setPosition({ top, left, transform });
  }, [tooltipPosition]);

  useEffect(() => {
    if (!tooltip) return;
    updatePosition();
    if (showTooltip) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [showTooltip, tooltip, updatePosition]);

  const p = SIZE_PADDING[size] || SIZE_PADDING.md;
  const iconSz = ICON_SIZE[size] || ICON_SIZE.md;
  const radius = buttonType === 'square' ? '0.5rem' : '50%';
  const ariaLabel = ariaLabelProp || tooltip;
  const IconComponent = loading ? Loader2 : Icon;
  const isDisabled = Boolean(props.disabled) || loading;

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        onClick={onClick}
        aria-label={ariaLabel}
        disabled={isDisabled}
        onMouseEnter={() => { setShowTooltip(true); updatePosition(); }}
        onMouseLeave={() => setShowTooltip(false)}
        className={`inline-flex items-center justify-center transition-all duration-150 cursor-pointer focus:outline-none focus-visible:ring-2 ${className ?? ''}`}
        style={{
          padding: p,
          borderRadius: radius,
          background: v.bg || 'transparent',
          color: v.color,
          border: 'none',
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = v.hoverBg || 'var(--bg-input)';
          if (v.hoverColor) e.currentTarget.style.color = v.hoverColor;
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = v.bg || 'transparent';
          e.currentTarget.style.color = v.color;
        }}
        {...props}
      >
        <IconComponent className={loading ? 'animate-spin' : undefined} style={{ width: iconSz, height: iconSz }} />
      </button>

      {tooltip && (
        <div
          className='fixed px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap z-[9999] pointer-events-none transition-opacity duration-200'
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: position.transform,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            color: 'var(--text-primary)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            opacity: showTooltip ? 1 : 0,
            visibility: showTooltip ? 'visible' : 'hidden',
          }}
        >
          {tooltip}
        </div>
      )}
    </>
  );
};

export default IconButton;
